import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiResponse } from '../models/api-response.model';
import { AuthService } from '../core/services/auth.service';

/**
 * Lo que el taller tiene pendiente y suele olvidar (GET /alertas): cobrar los
 * créditos, costear las piezas vendidas, cerrar las órdenes terminadas y llamar a
 * los clientes a los que les toca servicio. Es lo que muestra la campana.
 */
export interface ClienteDeudor {
  cliente_id: string;
  cliente_nombre: string;
  saldo: number;
  documentos: number;
  dias_max: number;
}

export interface ResumenAlertas {
  cobranza: {
    saldo_total: number;
    documentos: number;
    vencido: { saldo: number; documentos: number; dias: number };
    clientes: ClienteDeudor[];
  };
  costos_pendientes: { lineas: number; importe_venta: number };
  ordenes_por_cerrar: {
    total: number;
    a_credito: { count: number; saldo: number };
    sin_venta: { count: number; total: number };
  };
  recordatorios: { total: number; vencidos: number };
}

/** OS en FINALIZADO: a crédito espera su liquidación; sin venta nunca pasó por caja. */
export interface OrdenPorCerrar {
  id: string;
  folio: string;
  tipo: 'CREDITO' | 'SIN_VENTA';
  total: number;
  saldo_pendiente: number;
  venta_id?: string | null;
  venta_folio?: string | null;
  cliente_id?: string | null;
  cliente_nombre: string;
  vehiculo: string;
  fecha_finalizado?: string | null;
}

export interface ResultadoCierre {
  orden_id: string;
  folio: string | null;
  ok: boolean;
  accion: 'LIQUIDADA' | 'COBRADA' | null;
  mensaje: string;
  venta_folio?: string | null;
  monto?: number;
}

/** El backend procesa a lo más 20 por petición (API Gateway corta a los 30 s). */
export const TAMANO_TANDA_CIERRE = 20;

@Injectable({ providedIn: 'root' })
export class AlertasService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly apiUrl = environment.apiUrl;

  /** Último resumen cargado (la campana lo lee; las páginas lo refrescan al actuar). */
  readonly resumen = signal<ResumenAlertas | null>(null);

  private params(): Record<string, string> {
    const sucursalId = this.auth.currentUser()?.user?.sucursalActiva?.id;
    return sucursalId ? { sucursal_id: sucursalId } : {};
  }

  refrescar(): void {
    this.http.get<ApiResponse<ResumenAlertas>>(`${this.apiUrl}/alertas`, { params: this.params() })
      .pipe(map(r => r.data))
      .subscribe({ next: d => this.resumen.set(d), error: () => { } });
  }

  porCerrar(): Observable<OrdenPorCerrar[]> {
    return this.http.get<ApiResponse<{ items: OrdenPorCerrar[] }>>(`${this.apiUrl}/ordenes/por-cerrar`, { params: this.params() })
      .pipe(map(r => r.data?.items ?? []));
  }

  cerrarLote(payload: { orden_ids: string[]; metodo: string; forma_pago_sat?: string; referencia?: string; fecha_cierre?: string }):
    Observable<{ resultados: ResultadoCierre[]; cerradas: number; fallidas: number }> {
    const sucursal_id = this.auth.currentUser()?.user?.sucursalActiva?.id;
    return this.http.post<ApiResponse<{ resultados: ResultadoCierre[]; cerradas: number; fallidas: number }>>(
      `${this.apiUrl}/ordenes/cerrar-lote`, { ...payload, sucursal_id }).pipe(map(r => r.data));
  }
}
